import { useTranslation } from 'react-i18next';
import { S, Icon, formatCHF } from '../regureStyles';

const OverviewTab = ({
  portfolio,
  search,
  setSearch,
  filtered,
  period,
  setPeriod,
  periodOptions,
  isAdmin,
  onGenerateClick,
  onLinkPoliciesClick,
  linkingPolicies,
  onBrokerClick,
}) => {
  const { t } = useTranslation();
  const headers = [
    t('screens.commissions.colBroker'),
    t('screens.commissions.overview.productMix'),
    t('screens.commissions.overview.colRecurring'),
    t('screens.commissions.overview.colAcquisition'),
    t('screens.commissions.overview.colTotal'),
    t('screens.commissions.colPaid'),
    t('screens.commissions.colOutstanding'),
  ];

  // Portfolio premium first, then what it cost in commission — the order
  // Samire asked for. Rendered only when the aggregate actually loaded, so a
  // failed request costs the screen nothing.
  const P = portfolio?.totals;
  const stat = (label, value, accent) => (
    <div key={label} style={{ flex: '1 1 150px', minWidth: 140 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: accent || S.textPrimary, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value}</div>
    </div>
  );

  return (
    <>
      {P && (
        <div style={{ border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: 14, marginBottom: 14, background: '#fff' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: portfolio.byProduct?.length ? 12 : 0 }}>
            {stat(t('screens.commissions.portfolio.totalPremium'), formatCHF(P.premium), S.burgundy)}
            {stat(t('screens.commissions.portfolio.totalCommission'), formatCHF(P.commission))}
            {stat(t('screens.commissions.overview.colRecurring'), formatCHF(P.recurring))}
            {stat(t('screens.commissions.overview.colAcquisition'), formatCHF(P.acquisition))}
            {stat(t('screens.commissions.colOutstanding'), formatCHF(P.outstanding))}
            {portfolio.commissionRatio != null
              && stat(t('screens.commissions.portfolio.ratio'), `${portfolio.commissionRatio}%`)}
            {stat(t('screens.commissions.portfolio.policies'), String(P.policyCount ?? 0))}
          </div>
          {portfolio.byProduct?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {[t('screens.commissions.portfolio.product'),
                    t('screens.commissions.portfolio.premium'),
                    t('screens.commissions.overview.colRecurring'),
                    t('screens.commissions.overview.colAcquisition'),
                    t('screens.commissions.overview.colTotal')].map((h, i) => (
                      <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '6px 8px', fontSize: 10, color: S.textMuted, fontWeight: 700, textTransform: 'uppercase', borderBottom: `1px solid ${S.borderLight}` }}>{h}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {portfolio.byProduct.map(r => (
                  <tr key={r.product}>
                    <td style={{ padding: '6px 8px' }}>{r.product}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCHF(r.premium)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCHF(r.recurring)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCHF(r.acquisition)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{formatCHF(r.commission)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <div
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          >
            <Icon name='search' color='#94a3b8' size={15} />
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('screens.commissions.searchBrokers')}
            style={{
              width: '100%',
              padding: '9px 12px 9px 36px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              color: S.textPrimary,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: S.textMuted,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
            }}
          >
            {t('screens.commissions.overview.periodLabel')}
          </span>
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            style={{
              padding: '8px 12px',
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderRadius: 8,
              fontSize: 13,
              color: S.textPrimary,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {periodOptions.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        {isAdmin && (
          <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={onLinkPoliciesClick}
              disabled={linkingPolicies}
              title={t('screens.commissions.overview.linkPoliciesHint', {
                defaultValue: 'Assign brokers + infer product on any unlinked policies so Generate can produce records.',
              })}
              style={{
                padding: '9px 14px',
                background: '#fff',
                color: S.textPrimary,
                border: `1px solid ${S.borderLight}`,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: linkingPolicies ? 'wait' : 'pointer',
                opacity: linkingPolicies ? 0.6 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Icon name='link' color={S.textPrimary} size={14} />
              {linkingPolicies
                ? t('screens.commissions.overview.linkingPolicies', { defaultValue: 'Linking…' })
                : t('screens.commissions.overview.linkPoliciesButton', { defaultValue: 'Link Policies to Brokers' })}
            </button>
            <button
              onClick={onGenerateClick}
              style={{
                padding: '9px 14px',
                background: S.burgundy,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Icon name='refresh-ccw' color='#fff' size={14} />
              {t('screens.commissions.overview.generateButton')}
            </button>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            padding: '48px 24px',
            textAlign: 'center',
            background: '#fff',
            border: `1px dashed ${S.borderLight}`,
            borderRadius: 12,
            color: S.textMuted,
            fontSize: 13,
          }}
        >
          {t('screens.commissions.overview.noRecords')}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 8 }}>
            {t('screens.commissions.overview.clickBrokerHint')}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headers.map(h => (
                  <th
                    key={h}
                    style={{
                      padding: '12px 14px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: S.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      borderBottom: `2px solid ${S.borderLight}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => (
                <tr
                  key={b.id}
                  style={{
                    borderBottom: `1px solid ${S.borderLight}`,
                    cursor: 'pointer',
                    background: i % 2 === 0 ? '#fff' : S.bgCard,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
                  onClick={() => onBrokerClick && onBrokerClick(b.id)}
                >
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 700, color: S.textPrimary }}>
                    <div>{b.name}</div>
                    <div style={{ fontSize: 11, fontWeight: 400, color: S.textMuted }}>{b.manager}</div>
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 12, color: S.textSecondary }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(b.productMix || []).map(p => (
                        <span
                          key={p}
                          style={{
                            padding: '2px 7px',
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            background: '#f1f5f9',
                            color: S.textPrimary,
                            border: `1px solid ${S.borderLight}`,
                          }}
                        >
                          {p}
                        </span>
                      ))}
                      {(!b.productMix || b.productMix.length === 0) && <span style={{ color: S.textMuted }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>{formatCHF(b.recurring)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: S.burgundy, fontWeight: 600 }}>{formatCHF(b.acquisition)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 700, color: S.textPrimary }}>{formatCHF(b.earned)}</td>
                  <td style={{ padding: '13px 14px', fontSize: 13, color: '#10b981', fontWeight: 600 }}>{formatCHF(b.paid)}</td>
                  <td
                    style={{
                      padding: '13px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: b.outstanding > 0 ? '#f59e0b' : S.textMuted,
                    }}
                  >
                    {b.outstanding > 0 ? formatCHF(b.outstanding) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

export default OverviewTab;
